<template>
    <v-app>
        <v-app-bar app density="compact">
            <v-app-bar-nav-icon :to="{ path: '/dashboard' }" aria-label="Tunnel Twin">
                <img src="/img/tunneltwin_favicon-300x300.png" alt="" width="24" height="24" />
            </v-app-bar-nav-icon>
                <v-breadcrumbs :items="[{title: 'Tunnel Twin', href: '/dashboard'}, { title: route.params.projectName }]" class="font-weight-medium"></v-breadcrumbs>
                <v-spacer></v-spacer>
                <v-slider
                        class="mr-5 pa-0"
                        v-model="trajectoryStation"
                        :min="trajectoryStationMinimum"
                        :max="trajectoryStationMaximum"
                        :step="0.5"
                        :disabled="trajectoryLength <= 0"
                        @wheel.stop.prevent="adjustTrajectoryStationFromWheel"
                        hide-details
                        thumb-size="12"
                        density="compact">

                    <template v-slot:prepend>
                        <v-text-field
                                v-model.number="trajectoryStation"
                                type="number"
                                step="0.5"
                                density="compact"
                                variant="plain"
                                class="m-0 p-0"
                                :style="trajectoryStationInputStyle"
                                hide-details
                                ></v-text-field>
                    </template>
                </v-slider>
                <v-btn icon="mdi-pencil-plus" v-if="!viewer.currentCommand.value" @click="createPolyline" density="compact" v-tooltip:bottom="'Shortcut: P'">  </v-btn>
                <v-btn icon="mdi-map-marker" v-if="!viewer.currentCommand.value" @click="createMarker" density="compact" v-tooltip:bottom="'Shortcut: M'"></v-btn>
                <v-btn icon="mdi-ruler" v-if="!viewer.currentCommand.value" @click="getDistance" density="compact" v-tooltip:bottom="'Shortcut: D'"></v-btn>
                <v-btn icon="mdi-check" v-if="viewer.currentCommand.value" @click="acceptCommand" density="compact" color="green" v-tooltip:bottom="'Shortcut: Enter'"></v-btn>
                <v-btn icon="mdi-cancel" v-if="viewer.currentCommand.value" @click="cancelCommand" density="compact" color="red" v-tooltip:bottom="'Shortcut: Escape'"></v-btn>
                <v-btn icon="mdi-delete-outline" @click="deleteSelection" :disabled="selectedLines.length === 0" density="compact" v-tooltip:bottom="'Shortcut: Delete'"> </v-btn>
                <v-btn icon="mdi-undo" :disabled="viewer.undoStack.undo.length === 0" @click="undo" density="compact" v-tooltip:bottom="'Shortcut: U'"> </v-btn>
                <v-btn icon="mdi-redo" :disabled="viewer.undoStack.redo.length === 0" @click="redo" density="compact" v-tooltip:bottom="'Shortcut: R'"> </v-btn>
                <v-btn icon="mdi-window-maximize" :active="floatingWindowVisible" :disabled="!hasActiveMetadata" @click=floatingWindowToggle density="compact"> </v-btn>

                <v-spacer></v-spacer>
                <v-menu offset-y>
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" text>
                            {{ email }}
                            <v-icon right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item title="Profile" value="Profile" @click="onProfile" density="compact" prepend-icon="mdi-account-circle"/>
                        <v-list-item v-if="isAdmin" title="Create New Project" value="Create New Project" @click="onCreateProject" density="compact" prepend-icon="mdi-folder-plus-outline"/>
                        <v-list-item title="Project Studio" value="Project Studio" @click="onProjectStudio" density="compact" prepend-icon="mdi-pencil-ruler"/>
                        <v-list-item title="Save" value="Save" @click="onSave" density="compact" prepend-icon="mdi-content-save"/>
                        <v-list-item title="Settings" value="Settings" @click="settingsDialogVisible = true" density="compact" prepend-icon="mdi-cog-outline"/>
                        <v-list-item title="Import" value="Import" @click="openFilePicker" density="compact" prepend-icon="mdi-file-import-outline"/>
                        <v-list-item title="Export" value="Export" @click="openExportJson" density="compact" prepend-icon="mdi-file-export-outline"/>
                        <v-list-item title="Load" value="Load" @click="importModelFromFile" density="compact" prepend-icon="mdi-cube-outline"/>
                        <v-list-item title="Logout" value="Logout" @click="onLogout" density="compact" prepend-icon="mdi-logout"/>
                    </v-list>
                </v-menu>
            </v-app-bar>
            <v-navigation-drawer app width="300">
                <v-text-field
                        v-model="searchQuery"
                        density="compact"
                        hide-details
                        single-line
                        clearable
                        variant="outlined"
                        prepend-inner-icon="mdi-magnify"
                        placeholder="Search models, trajectories, lines and markers"
                        class="object-search"
                        ></v-text-field>
                <v-list v-model:opened="openTreeNodes" density="compact" nav class="object-tree">
                    <v-list-group value="models">
                        <template v-slot:activator="{ props, isOpen }">
                            <v-list-item v-bind="props" prepend-icon="mdi-cube-outline">
                                <v-list-item-title>Models ({{ modelTreeItems.length }})</v-list-item-title>
                                <template v-slot:append>
                                    <v-btn
                                            :icon="visibilityIcon(modelsAllVisible, modelsAnyVisible)"
                                            size="x-small"
                                            density="compact"
                                            variant="text"
                                            :disabled="enabledModelTreeItems.length === 0"
                                            @click.stop="toggleAllModelsVisibility"
                                            v-tooltip:bottom="'Toggle all models'"
                                            />
                                    <v-btn
                                            icon="mdi-crosshairs-gps"
                                            size="x-small"
                                            density="compact"
                                            variant="text"
                                            :disabled="visibleModelObjects.length === 0"
                                            @click.stop="zoomToModels"
                                            v-tooltip:bottom="'Zoom to visible models'"
                                            />
                                    <v-icon size="small" :icon="isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"></v-icon>
                                </template>
                            </v-list-item>
                        </template>
                        <v-list-item
                                v-for="item in filteredModelTreeItems"
                                :key="item.region.id"
                                :title="item.region.label"
                                :subtitle="item.subtitle"
                                :active="item.active"
                                :disabled="!item.region.enabled"
                                class="tree-item"
                                @click="activateModel(item.region.id)"
                                >
                            <template v-slot:prepend>
                                <v-checkbox-btn
                                    :model-value="item.loaded"
                                    :indeterminate="item.loading"
                                    :disabled="!item.region.enabled || item.loading"
                                    color="primary"
                                    density="compact"
                                    @click.stop
                                    @update:model-value="toggleModelLoaded(item, $event)"
                                    v-tooltip:bottom="item.loaded
                                        ? 'Unload model'
                                        : item.region.canActivate ? 'Load model and make region current' : 'Load model'"
                                    />
                            </template>
                            <template v-slot:append>
                                <v-btn
                                    :icon="item.active ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'"
                                    size="x-small"
                                    density="compact"
                                    variant="text"
                                    :color="item.active ? 'primary' : undefined"
                                    :disabled="!item.region.canActivate || item.loading"
                                    @click.stop="activateModel(item.region.id)"
                                    v-tooltip:bottom="item.region.canActivate ? 'Select current region' : 'This model has no region metadata'"
                                    />
                                <v-btn
                                    icon="mdi-crosshairs-gps"
                                    size="x-small"
                                    density="compact"
                                    variant="text"
                                    :disabled="!item.region.enabled || item.loading"
                                    @click.stop="zoomToModel(item.region.id)"
                                    v-tooltip:bottom="'Zoom to model'"
                                    />
                            </template>
                        </v-list-item>
                    </v-list-group>

                    <v-list-group value="trajectories">
                        <template v-slot:activator="{ props, isOpen }">
                            <v-list-item v-bind="props" prepend-icon="mdi-routes">
                                <v-list-item-title>Trajectories ({{ trajectoryTreeItems.length }})</v-list-item-title>
                                <template v-slot:append>
                                    <v-btn
                                            :icon="trajectoryVisible ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                                            size="x-small"
                                            density="compact"
                                            variant="text"
                                            :disabled="trajectoryLength <= 0"
                                            @click.stop="toggleTrajectoryFromTree"
                                            v-tooltip:bottom="trajectoryVisible ? 'Hide trajectory' : 'Show trajectory'"
                                            />
                                    <v-btn
                                            icon="mdi-crosshairs-gps"
                                            size="x-small"
                                            density="compact"
                                            variant="text"
                                            :disabled="trajectoryLength <= 0"
                                            @click.stop="zoomToTrajectory"
                                            v-tooltip:bottom="'Zoom to active trajectory'"
                                            />
                                    <v-icon size="small" :icon="isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"></v-icon>
                                </template>
                            </v-list-item>
                        </template>
                        <v-list-item
                                v-for="trajectoryItem in filteredTrajectoryTreeItems"
                                :key="trajectoryItem.id"
                                :title="trajectoryItem.label"
                                :subtitle="trajectoryItem.subtitle"
                                :active="trajectoryItem.active"
                                prepend-icon="mdi-vector-polyline"
                                class="tree-item"
                                @click="activateTrajectory(trajectoryItem.id)"
                                >
                            <template v-slot:append>
                                <v-icon
                                    size="small"
                                    :color="trajectoryItem.active ? 'primary' : undefined"
                                    :icon="trajectoryItem.active ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'"
                                    />
                            </template>
                        </v-list-item>
                    </v-list-group>

                    <v-list-group value="lines">
                        <template v-slot:activator="{ props, isOpen }">
                            <v-list-item v-bind="props" prepend-icon="mdi-vector-polyline">
                                <v-list-item-title>Lines ({{ lineTreeItems.length }})</v-list-item-title>
                                <template v-slot:append>
                                    <v-btn
                                        :icon="visibilityIcon(linesAllVisible, linesAnyVisible)"
                                        size="x-small"
                                        density="compact"
                                        variant="text"
                                        :disabled="lineTreeItems.length === 0"
                                        @click.stop="toggleAllLineVisibility"
                                        v-tooltip:bottom="'Toggle all lines'"
                                        />
                                    <v-btn
                                        icon="mdi-crosshairs-gps"
                                        size="x-small"
                                        density="compact"
                                        variant="text"
                                        :disabled="lineTreeItems.length === 0"
                                        @click.stop="zoomToLines"
                                        v-tooltip:bottom="'Zoom to lines'"
                                        />
                                    <v-icon size="small" :icon="isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"></v-icon>
                                </template>
                            </v-list-item>
                        </template>
                        <v-list-item
                                v-for="line in lineTreeItems"
                                :key="line.uuid()"
                                :title="line.name"
                                @click.shift.exact="handlePropPanelClick(line, lineIndex(line), true, false)"
                                @click.ctrl.exact="handlePropPanelClick(line, lineIndex(line), false, true)"
                                @click.exact="handlePropPanelClick(line, lineIndex(line), false, false)"
                                :active="selectedLines.includes(line)"
                                prepend-icon="mdi-vector-polyline"
                                class="tree-item"
                                style="user-select: none;"
                                >
                            <template v-slot:append>
                                <v-btn
                                    :icon="line.obj.visible ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                                    size="x-small"
                                    density="compact"
                                    variant="text"
                                    @click.stop="toggleLineVisibility(line)"
                                    v-tooltip:bottom="line.obj.visible ? 'Hide line' : 'Show line'"
                                    />
                                <v-btn
                                    icon="mdi-crosshairs-gps"
                                    size="x-small"
                                    density="compact"
                                    variant="text"
                                    @click.stop="handlePropPanelZoomTo(line)"
                                    v-tooltip:bottom="'Zoom to line'"
                                    />
                            </template>
                        </v-list-item>
                    </v-list-group>

                    <v-list-group value="markers">
                        <template v-slot:activator="{ props, isOpen }">
                            <v-list-item v-bind="props" prepend-icon="mdi-map-marker-outline">
                                <v-list-item-title>Markers ({{ markerTreeItems.length }})</v-list-item-title>
                                <template v-slot:append>
                                    <v-btn
                                        :icon="visibilityIcon(markersAllVisible, markersAnyVisible)"
                                        size="x-small"
                                        density="compact"
                                        variant="text"
                                        :disabled="markerTreeItems.length === 0"
                                        @click.stop="toggleAllMarkerVisibility"
                                        v-tooltip:bottom="'Toggle all markers'"
                                        />
                                    <v-btn
                                        icon="mdi-crosshairs-gps"
                                        size="x-small"
                                        density="compact"
                                        variant="text"
                                        :disabled="markerTreeItems.length === 0"
                                        @click.stop="zoomToMarkers"
                                        v-tooltip:bottom="'Zoom to markers'"
                                        />
                                    <v-icon size="small" :icon="isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"></v-icon>
                                </template>
                            </v-list-item>
                        </template>
                        <v-list-item
                                v-for="marker in markerTreeItems"
                                :key="marker.uuid()"
                                :title="marker.name"
                                @click.shift.exact="handlePropPanelClick(marker, lineIndex(marker), true, false)"
                                @click.ctrl.exact="handlePropPanelClick(marker, lineIndex(marker), false, true)"
                                @click.exact="handlePropPanelClick(marker, lineIndex(marker), false, false)"
                                :active="selectedLines.includes(marker)"
                                prepend-icon="mdi-map-marker"
                                class="tree-item"
                                style="user-select: none;"
                                >
                            <template v-slot:append>
                                <v-btn
                                    :icon="marker.obj.visible ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                                    size="x-small"
                                    density="compact"
                                    variant="text"
                                    @click.stop="toggleLineVisibility(marker)"
                                    v-tooltip:bottom="marker.obj.visible ? 'Hide marker' : 'Show marker'"
                                    />
                                <v-btn
                                    icon="mdi-crosshairs-gps"
                                    size="x-small"
                                    density="compact"
                                    variant="text"
                                    @click.stop="handlePropPanelZoomTo(marker)"
                                    v-tooltip:bottom="'Zoom to marker'"
                                    />
                            </template>
                        </v-list-item>
                    </v-list-group>

                    <v-list-group value="pointClouds">
                        <template v-slot:activator="{ props, isOpen }">
                            <v-list-item v-bind="props" prepend-icon="mdi-map-marker-outline">
                                <v-list-item-title>Point Clouds ({{ pointCloudTreeItems.length }})</v-list-item-title>
                                <template v-slot:append>
                                    <v-btn
                                        :icon="visibilityIcon(pointCloudsAllVisible, pointCloudsAnyVisible)"
                                        size="x-small"
                                        density="compact"
                                        variant="text"
                                        :disabled="pointCloudTreeItems.length === 0"
                                        @click.stop="toggleAllPointCloudVisibility"
                                        v-tooltip:bottom="'Toggle all point clouds'"
                                        />
                                    <v-btn
                                        icon="mdi-crosshairs-gps"
                                        size="x-small"
                                        density="compact"
                                        variant="text"
                                        :disabled="pointCloudTreeItems.length === 0"
                                        @click.stop="zoomToPointClouds"
                                        v-tooltip:bottom="'Zoom to point clouds'"
                                        />
                                    <v-icon size="small" :icon="isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"></v-icon>
                                </template>
                            </v-list-item>
                        </template>
                        <v-list-item
                                v-for="pointCloud in pointCloudTreeItems"
                                :key="pointCloud.id"
                                :title="pointCloud.name"
                                :subtitle="pointCloud.loading ? 'Loading...' : ''"
                                class="tree-item"
                                style="user-select: none;"
                                >
                            <template v-slot:prepend>
                                <v-checkbox-btn
                                    :model-value="pointCloud.loaded"
                                    :indeterminate="pointCloud.loading"
                                    :disabled="pointCloud.loading"
                                    color="primary"
                                    density="compact"
                                    @click.stop
                                    @update:model-value="togglePointCloudLoaded(pointCloud, $event)"
                                    v-tooltip:bottom="pointCloud.loaded ? 'Unload model' : 'Load model'"
                                    />
                            </template>
                            <template v-slot:append>
                                <v-btn
                                    :icon="pointCloud.visible ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                                    :disabled="pointCloud.loading"
                                    size="x-small"
                                    density="compact"
                                    variant="text"
                                    @click.stop="togglePointCloudVisibility(pointCloud)"
                                    v-tooltip:bottom="pointCloud.visible ? 'Hide point cloud' : 'Show point cloud'"
                                    />
                                <v-btn
                                    :disabled="pointCloud.loading"
                                    icon="mdi-crosshairs-gps"
                                    size="x-small"
                                    density="compact"
                                    variant="text"
                                    @click.stop="zoomToPointCloud(pointCloud)"
                                    v-tooltip:bottom="'Zoom to point cloud'"
                                    />
                            </template>
                        </v-list-item>
                    </v-list-group>

                </v-list>
            </v-navigation-drawer>
            <v-navigation-drawer width="320" v-if="selectedLines.length === 1">
                <v-card>
                    <v-card-text fill-height>
                        <v-text-field v-model="selectedLines[0].name" label="Name" density="compact" slim type="input" clearable></v-text-field>
                        <v-combobox v-model="severityRef" slim density="compact" label="Severity" :items="severityItems" @update:modelValue="onChangeSeverity"></v-combobox>
                        <!-- <v-autocomplete slim density="compact" label="Type" :items="['Line', 'Crack', 'Light', 'Sign']"></v-autocomplete> -->
                        <v-text-field v-model="selectedLines[0].url" 
                                      label="URL" 
                                      density="compact" 
                                      slim 
                                      type="input" 
                                      clearable
                                      append-icon="mdi-open-in-new" 
                                      @update:model-value="validateUrl"
                                      @click:append="openUrl"
                                      ></v-text-field>
                        <v-text-field v-model="selectedLines[0].createdBy" label="Created by" density="compact" slim type="input" clearable></v-text-field>
                        <v-text-field v-for="(value, key) in selectedLines[0].custom" :key="key"
                            v-model="selectedLines[0].custom[key]"
                            :label="key"
                            density="compact"
                            slim
                            clearable
                            class="my-0 py-0"
                            >
                        </v-text-field>

                        <v-confirm-edit v-if="addPropertyEdit" class="mt-5" ok-text="Ok" cancel-text="Cancel" @cancel="cancelAddProperty" @save="addProperty" v-model="addPropertyModel" density="compact">
                            <template v-slot:default="{ model: proxyModel, actions }">
                                <v-card class="mx-auto" title="Add property" density="compact">
                                    <template v-slot:text>
                                        <v-text-field v-model="proxyModel.value" messages="Name" density="compact"></v-text-field>
                                    </template>
                                    <template v-slot:actions>
                                        <v-spacer></v-spacer>
                                        <component :is="actions"></component>
                                    </template>
                                </v-card>
                            </template>
                        </v-confirm-edit>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn icon="mdi-plus" @click="addPropertyEdit = true" text="Add Property"></v-btn>
                    </v-card-actions>
                </v-card>
            </v-navigation-drawer>
            <v-main class="viewer-main">
                <v-alert
                        v-if="regionErrorMessage"
                        class="region-error"
                        type="warning"
                        density="compact"
                        closable
                        variant="tonal"
                        >{{ regionErrorMessage }}</v-alert>
                <v-container fluid class="viewer-content">
                    <div ref="canvasDiv" id="mainCanvasDivId" class="viewport" :class="{ 'command-crosshair': precisionCommandActive }">
                        <canvas ref="canvasRef"></canvas>
                    </div>
                    <div class="cursor-status-bar">
                        <span class="cursor-coordinate">{{ cursorPositionText }}</span>
                    </div>
                </v-container>
                <v-dialog v-model="settingsDialogVisible" max-width="600">
                    <v-card>
                        <v-card-title>
                            <span class="text-h6">Settings</span>
                        </v-card-title>
                        <v-card-text>
                            <v-checkbox label="Cameras visible" v-model="camerasVisible" :disabled="!hasActiveMetadata" @change="toggleCamerasVisibility" density="compact"></v-checkbox>
                            <v-checkbox label="Trajectory visible" v-model="trajectoryVisible" :disabled="trajectoryLength <= 0" @change="toggleTrajectoryVisibility" density="compact"></v-checkbox>
                            <v-checkbox label="Compress model textures (model reload required)" v-model="textureCompression" @change="toggleTextureCompression" density="compact"></v-checkbox>
                            <v-slider label="Line width" v-model="lineWidthModel" min="0.0007" max="0.07" @update:modelValue="changeLineWidthModel" density="compact"></v-slider>
                            <v-slider label="Light" v-model="lightValue" min="0.0" :max="lightValueMax" @update:modelValue="changeLightValue" density="compact"></v-slider>
                        </v-card-text>
                    </v-card>
                </v-dialog>

                <div v-if="floatingWindowVisible" class="floating-window" :style="windowStyles" @mousedown="onFloatWindowMouseDown">
                    <div class="floating-window-header" @mousedown.stop="onFloatWindowDragStart">
                        <span>{{viewer.imageLabel}} </span> 
                        <span> Flip: <v-btn icon="mdi-flip-horizontal" @click="flipImage" slim size="x-small" flat rounded="0" ></v-btn> </span>
                        <v-btn icon="mdi-close" @click="floatingWindowToggle" slim size="x-small" flat rounded="0"></v-btn>
                        
                    </div>
                    <div ref="imageCanvasDivRef" class="viewport" :class="{ 'command-crosshair': precisionCommandActive }">
                        <canvas ref="imageCanvasRef"></canvas>
                    </div>
                    <div class="resize-handle top-left" @mousedown.stop.prevent="onFloatWindowResizeStart('top-left', $event)"></div>
                    <div class="resize-handle top-right" @mousedown.stop.prevent="onFloatWindowResizeStart('top-right', $event)"></div>
                    <div class="resize-handle bottom-left" @mousedown.stop.prevent="onFloatWindowResizeStart('bottom-left', $event)"></div>
                    <div class="resize-handle bottom-right" @mousedown.stop.prevent="onFloatWindowResizeStart('bottom-right', $event)"></div>
                </div>
                <v-snackbar v-model="snackbar" :timeout="snackbarTimeout">
                    {{ snackbarTextComputed() }}
                </v-snackbar>
            </v-main>
        </v-app>
        <svg style="display: none;">
            <symbol id="mdi-map-marker" viewBox="0 0 24 24"> <path fill="white" d="M12 11.5A2.5 2.5 0 0 1 9.5 9A2.5 2.5 0 0 1 12 6.5A2.5 2.5 0 0 1 14.5 9a2.5 2.5 0 0 1-2.5 2.5M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7" /> </symbol>
        </svg>
</template>

<script setup lang="ts">

import { computed, onBeforeMount, onMounted, onUnmounted, nextTick, defineComponent, useTemplateRef, ref, shallowRef, watch, reactive } from 'vue';
import { useCurrentUser, useFirestore, useFirebaseAuth } from 'vuefire';
import { useRoute } from 'vue-router';
import router from '../router';
import * as THREE from 'three';
import { cameraTrajectoryId, createViewer, getViewer, destroyViewer, type Viewer, type ProjectPointCloud } from '/components/ttViewer';
import type { NormalizedProjectRegion } from '/components/ttProjectConfig';
import type { Polyline, PointCloud } from '/components/ttObject';
import { onProfile, onLogout, onCreateProject, isAdminEmail } from '/components/ttUtils';
import * as ttCmd from '/components/ttCommands';
import { lineMaterialModel, lineMaterialImage } from '/components/ttMaterials';

const route = useRoute();

function onProjectStudio() {
    router.push({ path: '/createproject/', query: { project: String(route.params.projectName) } });
}
const user = useCurrentUser();
const auth = useFirebaseAuth();
const email = auth?.currentUser?.email;
const db = useFirestore();
const isAdmin = ref(false);
if (email)
    isAdminEmail(db, email).then((v) => { isAdmin.value = v; }).catch(() => { isAdmin.value = false; });
let viewer : Viewer | null = null;
let isRunning = false;
const viewerEpoch = ref(0);

const canvasDiv = useTemplateRef<HTMLElement | null>("canvasDiv");
const canvasRef = useTemplateRef<HTMLCanvasElement | null>("canvasRef");
const renderer = ref<THREE.WebGLRenderer | null>(null);
const imageCanvasDivRef = useTemplateRef<HTMLElement | null>("imageCanvasDivRef");
const imageCanvasRef = useTemplateRef<HTMLCanvasElement | null>("imageCanvasRef");
const imageRenderer = ref<THREE.WebGLRenderer | null>(null);
const fileInput = useTemplateRef<HTMLInputElement | null>(null);
const openTreeNodes = ref<string[]>([]);
const pendingRegionIds = ref<string[]>([]);

const isResizingViewports = ref<bool>(false);
const leftWidth = ref(50);
const tabs = ref(null);

let resizeObserver = null;

const variesStr = '*Varies*'
const createdBy = ref('');
const severityIndex = ref(0);
const lines = ref<Polyline[]>([]);
const selectedLines = ref<Polyline[]>([]);
const pointClouds = ref<ProjectPointCloud[]>([]);
const pendingPointCloudsIds = ref<string[]>([]);

onBeforeMount(() => {
    createViewer(user, String(route.params.projectName));
    viewer = getViewer();
    configureViewer(viewer);
    viewerEpoch.value++;
});

watch(() => route.params.projectName, async (newProjectName, oldProjectName) => {
    if (!isRunning || newProjectName === oldProjectName) return;
    viewer?.onUnmounted();
    createViewer(user, String(newProjectName));
    viewer = getViewer();
    configureViewer(viewer);
    viewerEpoch.value++;
    if (viewer && canvasRef.value && renderer.value) {
        viewer.setMainCanvas(canvasRef.value, renderer.value);
        await viewer.afterMount();
        viewerEpoch.value++;
        lines.value = viewer.lines;
        selectedLines.value = viewer.selection.objects;
    }
});

onMounted(async () => {
    await nextTick();
    window.addEventListener("resize", doWindowResize);
    if (canvasRef.value) {
        renderer.value = new THREE.WebGLRenderer({
            logarithmicDepthBuffer: true,
            //depth: false,
            canvas: canvasRef.value,
            antialias: true,
        })
        //renderer.value.shadowMap.enabled = true;
        //renderer.value.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.value.autoClear = false;
        renderer.value.setSize(canvasRef.value.clientWidth, canvasRef.value.clientHeight, false);
        renderer.value.setPixelRatio(window.devicePixelRatio);
        renderer.value.setClearColor(0x333333, 1);
        viewer.setMainCanvas(canvasRef.value, renderer.value);
    }
    else {
        console.error("Invalid canvasRef.value");
    }

    await viewer.afterMount();
    viewerEpoch.value++;
    lines.value = viewer.lines;
    selectedLines.value = viewer.selection.objects;
    pointClouds.value = viewer.projectPointClouds;
    isRunning = true;

    resizeObserver = new ResizeObserver(() => {
        if (animationFrameId !== null) {
            cancelAnimationFrame(animationFrameId);
        }
        nextTick(() => {
            handleResize();
            animate();
        });
    });
    resizeObserver.observe(canvasDiv.value);

    animate();
});

onUnmounted(async () => {
    isRunning = false;
    viewer.onUnmounted();
    lines.value = [];
    selectedLines.value = [];
    pointClouds.value = [];

    window.removeEventListener("resize", doWindowResize);
    document.removeEventListener('mousemove', resizeViewports)
    document.removeEventListener('mouseup', stopResizingViewports)

    document.removeEventListener('mousemove', onFloatingWindowDrag);
    document.removeEventListener('mouseup', onFloatingWindowDragEnd);
    document.removeEventListener('mousemove', onFloatingWindowResize);
    document.removeEventListener('mouseup', onFloatingWindowResizeEnd);
    viewer = null;

    if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
    }
    destroyViewer();
});

function handleResize() {
    if (!canvasDiv.value)
        return;
    const brect = canvasDiv.value.getBoundingClientRect();
    renderer.value!.setSize(brect.width, brect.height, false);
    viewer.camera.aspect = brect.width / brect.height;
    viewer.camera.updateProjectionMatrix();
    viewer.controls.update();
    viewer.updateOnControlChange();
}

async function doWindowResize(event) {
    await nextTick();
    handleResize();
}

function startResizingViewports(event) {
    isResizingViewports.value = true;
    document.addEventListener('mousemove', resizeViewports)
    document.addEventListener('mouseup', stopResizingViewports)
}

async function resizeViewports(event) {
    await nextTick();
    if (isResizingViewports.value) {
        const containerWieth = document.querySelector('.v-container').offsetWidth
        const newLeftWidth = (event.clientX / containerWidth) * 100
        if (newLeftWidth >= 1 && newLeftWidth <= 99) {
            leftWidth.value = newLeftWidth
        }
    }
    handleResize();
}

function stopResizingViewports() {
    isResizingViewports.value = false
    document.removeEventListener('mousemove', resizeViewports)
    document.removeEventListener('mouseup', stopResizingViewports)
}

let animationFrameId = null;
function animate() {
    if (!isRunning)
        return;
    if (viewer) {
        animationFrameId = requestAnimationFrame(animate);
        renderer.value!.clear();
        if (viewer.controls)
            viewer.controls.update();
        renderer.value!.render(viewer.scene, viewer.camera);
        viewer.viewHelper.render(renderer.value);
        if (viewer.stats)
            viewer.stats.update();
    }
}

let imageAnimateFrameId = null;
function imageAnimate() {
    if (!isRunning) return;
    if (viewer) {
        imageAnimateFrameId = requestAnimationFrame(imageAnimate);
        imageRenderer.value!.clear();
        if (viewer.orthoControls)
            viewer.orthoControls.update();
        imageRenderer.value!.render(viewer.imageScene, viewer.orthoCamera);
    }
}

watch(selectedLines, () => {
        if (selectedLines.value.length === 1) {
            severityRef.value = severityItems[selectedLines.value[0].severity];
        }
        else {
            severityRef.value = null;
        }
    }
    , { deep: true, }
);

function onChangeSeverity() {
    if (!viewer) return;
    if (severityRef.value) {
        for (const obj of selectedLines.value) {
            obj.severity = severityRef.value.value;
        }
    }
};

const severityItems = [
    { title: "Unknown", value: 0, },
    { title: "Low", value: 1, },
    { title: "Normal", value: 2, },
    { title: "High", value: 3, },
];
const severityRef = ref(severityItems[1]);

const onChangeCreatedBy = (e: Event) => {
    if (!viewer) return;
    for (const obj of viewer.selection.objects) {
        obj.createdBy = createdBy.value;
    }
};

let previousPanelClickIndex = -1;
function handlePropPanelClick(line, index: number, shift: boolean, control: boolean) {
    cancelAddProperty();
    if (!viewer) {
        return
    }
    if (shift) {
        if (previousPanelClickIndex === index) {
            viewer.selection.remove(line);
        }
        else {
            if (previousPanelClickIndex < 0) {
                viewer.selection.add(line);
                previousPanelClickIndex = index;
            }
            else {
                viewer.selection.addRange(previousPanelClickIndex, index);
            }
        }
    }
    else {
        if (viewer.selection.has(line)) {
            viewer.selection.remove(line);
        }
        else {
            if (control) {
                viewer.selection.add(line);
            }
            else {
                viewer.selection.clear();
                viewer.selection.add(line);
            }
        }
    }
    previousPanelClickIndex = index;
}

const settingsDialogVisible = ref(false);

function handlePropPanelZoomTo(line: Polyline) {
    if (viewer) {
        viewer.zoomTo(line.obj);
    }
}

const addPropertyEdit = ref(false);
const addPropertyModel = shallowRef("");

function addProperty() {
    console.log(addPropertyModel.value);
    selectedLines.value[0].custom[addPropertyModel.value] = "";
    addPropertyModel.value = "";
    addPropertyEdit.value = false;
}

function cancelAddProperty() {
    addPropertyEdit.value = false;
    addPropertyModel.value = '';
}

const floatingWindowVisible = ref(false);
const floatingWinndowPos = reactive({ x: 400, y: 300 });
const floatingWinndowSize = reactive({ width: 600, height: 480 });
const isDragging = ref(false);
const isResizing = ref(false);
const resizeDirection = ref('');
const startMousePosition = reactive({ x: 0, y: 0 });
const startWindowSize = reactive({ width: 0, height: 0 });
const startWindowPosition = reactive({ x: 400, y: 300 });

const windowStyles = computed(() =>
({
    left: `${floatingWinndowPos.x}px`,
    top: `${floatingWinndowPos.y}px`,
    width: `${floatingWinndowSize.width}px`,
    height: `${floatingWinndowSize.height}px`,
}));

function updateImageWindowParams() {
    if (!imageCanvasDivRef.value || !imageRenderer.value || !viewer) return;
    const rect = imageCanvasDivRef.value.getBoundingClientRect();
    imageRenderer.value.setSize(rect.width, rect.height, false);
    for (const material of lineMaterialImage) material.resolution.set(rect.width, rect.height);
    viewer.refreshImageViewport();
}

async function floatingWindowToggle() {
    floatingWindowVisible.value = !floatingWindowVisible.value;
    if (floatingWindowVisible.value) {
        await nextTick();
        const imageCanvas = imageCanvasRef.value;
        if (imageCanvas && viewer) {
            imageRenderer.value = new THREE.WebGLRenderer({
                logarithmicDepthBuffer: false,
                depth: false,
                canvas: imageCanvas,
                antialias: true,
            })
            imageRenderer.value.autoClear = false;
            imageRenderer.value.setPixelRatio(window.devicePixelRatio);
            const imageRect = imageCanvasDivRef.value?.getBoundingClientRect();
            const imageWidth = imageRect?.width ?? imageCanvas.clientWidth;
            const imageHeight = imageRect?.height ?? imageCanvas.clientHeight;
            imageRenderer.value.setSize(imageWidth, imageHeight, false);
            for (const material of lineMaterialImage) material.resolution.set(imageWidth, imageHeight);
            imageRenderer.value.setClearColor(0x333333, 1);
            viewer.setImageCanvas(imageCanvasRef.value, imageRenderer.value);
            imageAnimate();
        }
        else {
            console.error("Invalid imageCanvasRef.value");
        }
    }
    else {
        cancelAnimationFrame(imageAnimateFrameId);
        imageAnimateFrameId = null;
        viewer?.detachImageCanvas();
        imageRenderer.value?.dispose();
        imageRenderer.value = null;
    }
}

async function flipImage() {
    if (viewer){
        viewer.flipImage.value = !viewer.flipImage.value;
        viewer.resetImageViewport(viewer.flipImage.value);
    }
}

function onFloatWindowDragStart(event) {
    isDragging.value = true;
    startMousePosition.x = event.clientX;
    startMousePosition.y = event.clientY;
    startWindowPosition.x = floatingWinndowPos.x;
    startWindowPosition.y = floatingWinndowPos.y;
    document.addEventListener('mousemove', onFloatingWindowDrag);
    document.addEventListener('mouseup', onFloatingWindowDragEnd);
}

function onFloatingWindowDrag(event) {
    if (!isDragging.value) return;
    const dx = event.clientX - startMousePosition.x;
    const dy = event.clientY - startMousePosition.y;
    floatingWinndowPos.x = startWindowPosition.x + dx;
    floatingWinndowPos.y = startWindowPosition.y + dy;
}

function onFloatingWindowDragEnd() {
    isDragging.value = false;
    document.removeEventListener('mousemove', onFloatingWindowDrag);
    document.removeEventListener('mouseup', onFloatingWindowDragEnd);
}

function onFloatWindowResizeStart(direction: string, event: MouseEvent) {
    isResizing.value = true;
    resizeDirection.value = direction;
    startMousePosition.x = event.clientX;
    startMousePosition.y = event.clientY;
    startWindowSize.width = floatingWinndowSize.width;
    startWindowSize.height = floatingWinndowSize.height;
    startWindowPosition.x = floatingWinndowPos.x;
    startWindowPosition.y = floatingWinndowPos.y;
    document.addEventListener('mousemove', onFloatingWindowResize);
    document.addEventListener('mouseup', onFloatingWindowResizeEnd);
}

function onFloatingWindowResize(event) {
    if (!isResizing.value) return;
    const dx = event.clientX - startMousePosition.x;
    const dy = event.clientY - startMousePosition.y;

    switch (resizeDirection.value) {
        case 'top-left':
            floatingWinndowSize.width = startWindowSize.width - dx;
            floatingWinndowSize.height = startWindowSize.height - dy;
            floatingWinndowPos.x = startWindowPosition.x + dx;
            floatingWinndowPos.y = startWindowPosition.y + dy;
            break;
        case 'top-right':
            floatingWinndowSize.width = startWindowSize.width + dx;
            floatingWinndowSize.height = startWindowSize.height - dy;
            floatingWinndowPos.y = startWindowPosition.y + dy;
            break;
        case 'bottom-left':
            floatingWinndowSize.width = startWindowSize.width - dx;
            floatingWinndowSize.height = startWindowSize.height + dy;
            floatingWinndowPos.x = startWindowPosition.x + dx;
            break;
        case 'bottom-right':
            floatingWinndowSize.width = startWindowSize.width + dx;
            floatingWinndowSize.height = startWindowSize.height + dy;
            break;
    }
}

function onFloatingWindowResizeEnd() {
    isResizing.value = false;
    resizeDirection.value = '';
    document.removeEventListener('mousemove', onFloatingWindowResize);
    document.removeEventListener('mouseup', onFloatingWindowResizeEnd);
    updateImageWindowParams();
}

function undo() {
    if (viewer) {
        viewer.undo();
    }
}

function redo() {
    if (viewer) {
        viewer.redo();
    }
}

function createPolyline() {
    if (viewer) {
        viewer.createPolylineCommand();
    }
}

function createMarker() {
    if (viewer) {
        viewer.createMarkerCommand();
    }
}

function getDistance() {
    viewer?.getDistanceCommand();
}

function configureViewer(instance: Viewer | null) {
    if (instance) instance.onDistanceCommandStarted = showDistanceSnackbar;
}

function showDistanceSnackbar() {
    const fn = () => {
        if (viewer?.currentCommand.value instanceof ttCmd.GetDistanceCmd) {
            return `Length: ${viewer.currentCommand.value.length}`;
        }
        snackbarText.value = "";
        snackbar.value = false;
        return "";
    };
    showSnackbar(fn, -1);
}

function acceptCommand() {
    if (viewer) {
        viewer.endCommand();
    }
}

function cancelCommand() {
    if (viewer) {
        viewer.cancelCommand();
    }
}

function deleteSelection() {
    if (viewer && viewer.currentCommand.value === null) {
        let command = new ttCmd.DeleteLinesCmd(viewer);
        viewer.beginCommand(command);
        viewer.endCommand();
    }
}

async function onSave() {
    if (viewer) {
        await viewer.saveServer();
        showSnackbar("Data saved sucessfully");
    }
}

function openFilePicker() {
    //fileInput.value?.click();
        //select point cloud file and append the loaded point cloud to the current region. The point cloud file should be in .ply format.
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (!target.files || target.files.length === 0) {
            return;
        }
        if (viewer) {
            const files = Array.from(target.files);
            try {
                const importedData = await Promise.all(files.map((file) => {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                            try {
                                const json = JSON.parse(e.target?.result);
                                resolve(json);
                            }
                            catch (error) {
                                reject(new Error(`Invalid JSON in file ${file.name}`));
                            }
                        }
                        reader.onerror = (e) => reject(new Error(`Error reading file ${file.name}: ${e}`));
                        reader.readAsText(file);
                    });
                }));
                for (const fileData of importedData) {
                    await viewer.importJSON(fileData as Object);
                }
            }
            catch (error) {
               showSnackbar(`Error loading files: ` + files.map((file) => file.name) + `\nError: ${error}`);
            }
        }
    };
    input.click();
}

function openExportJson() {
    if (viewer) {
        viewer.exportJSON();
    }
}

const camerasVisible = ref(false);
function toggleCamerasVisibility() {
    if (viewer) {
        let cmd = new ttCmd.ToggleCamerasVisibilityCmd(viewer, camerasVisible.value);
        viewer.beginCommand(cmd);
        viewer.endCommand();
    }
}

const trajectoryVisible = ref(true);
function toggleTrajectoryVisibility() {
    if (viewer) {
        let cmd = new ttCmd.ToggleTrajectoryVisibilityCmd(viewer, trajectoryVisible.value);
        viewer.beginCommand(cmd);
        viewer.endCommand();
    }
}

const textureCompression = ref(true);
function toggleTextureCompression(){
    if (viewer) {
        let cmd = new ttCmd.ToggleTextureCompressionCmd(viewer, textureCompression.value);
        viewer.beginCommand(cmd);
        viewer.endCommand();
    }
}

const lineWidthModel = ref<number>(0.014);
function changeLineWidthModel() {
    if (viewer) {
        for (const lm of lineMaterialModel)
            lm.linewidth = lineWidthModel.value;
    }
}

const trajectoryStationOrigin = computed(() => {
    viewerEpoch.value;
    return viewer?.trajectoryStartStation.value ?? 0;
});

const trajectoryStationIncreasing = computed(() => {
    viewerEpoch.value;
    return viewer?.trajectoryStationIncreasing.value ?? true;
});

const trajectoryHorizontalLength = computed(() => {
    viewerEpoch.value;
    return viewer?.trajectoryHorizontalLength.value ?? 0;
});

const trajectoryStationMinimum = computed(() => trajectoryStationIncreasing.value
    ? trajectoryStationOrigin.value
    : trajectoryStationOrigin.value - trajectoryHorizontalLength.value);
const trajectoryStationMaximum = computed(() => trajectoryStationIncreasing.value
    ? trajectoryStationOrigin.value + trajectoryHorizontalLength.value
    : trajectoryStationOrigin.value);

const trajectoryStation = computed({
    get() {
        if (viewer) {
            const direction = viewer.trajectoryStationIncreasing.value ? 1 : -1;
            const station = viewer.trajectoryStartStation.value + direction * viewer.trajectoryHorizontalPos.value;
            return Math.round(station * 100) / 100;
        }
        return 0.0;
    },
    set(value) {
        const station = Number(value);
        if (!viewer || !Number.isFinite(station)) return;
        const direction = viewer.trajectoryStationIncreasing.value ? 1 : -1;
        viewer.setHorizontalStation(direction * (station - viewer.trajectoryStartStation.value));
    }

});

const trajectoryStationInputStyle = computed(() => {
    const text = Number(trajectoryStation.value.toFixed(2)).toString();
    return { width: `${Math.max(8, text.length + 4)}ch`, flex: '0 0 auto' };
});

function adjustTrajectoryStationFromWheel(event: WheelEvent) {
    if (!viewer || trajectoryHorizontalLength.value <= 0 || event.deltaY === 0) return;
    const direction = event.deltaY < 0 ? 1 : -1;
    trajectoryStation.value = THREE.MathUtils.clamp(
        trajectoryStation.value + direction * 0.5,
        trajectoryStationMinimum.value,
        trajectoryStationMaximum.value,
    );
}

const trajectoryLength = computed(() => {
    viewerEpoch.value;
    return viewer?.trajectoryLength.value ?? 0;
});

interface TrajectoryTreeItem {
    id: string;
    label: string;
    subtitle: string;
    active: boolean;
}

const trajectoryTreeItems = computed<TrajectoryTreeItem[]>(() => {
    viewerEpoch.value;
    viewer?.trajectoryStateVersion.value;
    if (!viewer) return [];
    if (viewer.customTrajectories.length) {
        return viewer.customTrajectories.map((trajectory) => ({
            id: trajectory.id,
            label: trajectory.label || trajectory.id,
            subtitle: `${trajectory.points.length} points`,
            active: viewer?.activeTrajectoryId.value === trajectory.id,
        }));
    }
    const activeRegion = viewer.availableRegions.find((region) => region.id === viewer?.activeRegionId.value);
    return [{
        id: cameraTrajectoryId,
        label: 'Camera-derived trajectory',
        subtitle: activeRegion ? `Current region: ${activeRegion.label}` : 'Available when region metadata is loaded',
        active: viewer.activeTrajectoryId.value === cameraTrajectoryId,
    }];
});

const filteredTrajectoryTreeItems = computed(() => {
    const query = (searchQuery.value ?? '').trim().toLocaleUpperCase();
    if (!query) return trajectoryTreeItems.value;
    return trajectoryTreeItems.value.filter((trajectory) => trajectory.label.toLocaleUpperCase().includes(query)
        || trajectory.id.toLocaleUpperCase().includes(query));
});

function activateTrajectory(trajectoryId: string) {
    if (!viewer || viewer.activeTrajectoryId.value === trajectoryId) return;
    if (trajectoryId === cameraTrajectoryId && !viewer.hasCustomTrajectories) return;
    if (viewer.setActiveTrajectory(trajectoryId)) viewerEpoch.value++;
}

function toggleTrajectoryFromTree() {
    trajectoryVisible.value = !trajectoryVisible.value;
    toggleTrajectoryVisibility();
}

function zoomToTrajectory() {
    if (!viewer) return;
    const trajectoryLine = viewer.scene.getObjectByName('trajectory');
    if (trajectoryLine) viewer.zoomToObjects([trajectoryLine]);
}

const hasActiveMetadata = computed(() => {
    viewerEpoch.value;
    return Boolean(viewer?.metashapeData && viewer?.activeRegionId.value);
});

interface ModelTreeItem {
    region: NormalizedProjectRegion;
    visible: boolean;
    loaded: boolean;
    active: boolean;
    loading: boolean;
    subtitle: string;
}

const modelTreeItems = computed<ModelTreeItem[]>(() => {
    viewerEpoch.value;
    viewer?.regionStateVersion.value;
    const selected = new Set(viewer?.selectedRegionIds.value ?? []);
    return (viewer?.availableRegions ?? []).map((region) => {
        const runtime = viewer?.getRegionRuntime(region.id);
        const active = viewer?.activeRegionId.value === region.id;
        let subtitle = '';
        if (!region.enabled) subtitle = 'Unavailable';
        else if (runtime?.status === 'loading') subtitle = 'Loading…';
        else if (runtime?.status === 'error') subtitle = 'Load failed';
        else if (active) subtitle = 'Current region';
        else if (region.validationError) subtitle = 'Model only';
        return {
            region,
            visible: selected.has(region.id),
            loaded: runtime?.status === 'loaded',
            active,
            loading: runtime?.status === 'loading' || pendingRegionIds.value.includes(region.id),
            subtitle,
        };
    });
});

const filteredModelTreeItems = computed(() => {
    const query = (searchQuery.value ?? '').trim().toLocaleUpperCase();
    if (!query) return modelTreeItems.value;
    return modelTreeItems.value.filter(({ region }) => {
        return region.label.toLocaleUpperCase().includes(query)
            || region.id.toLocaleUpperCase().includes(query);
    });
});

const enabledModelTreeItems = computed(() => modelTreeItems.value.filter((item) => item.region.enabled));
const modelsAllVisible = computed(() => enabledModelTreeItems.value.length > 0
    && enabledModelTreeItems.value.every((item) => item.visible));
const modelsAnyVisible = computed(() => enabledModelTreeItems.value.some((item) => item.visible));
const visibleModelObjects = computed(() => {
    viewerEpoch.value;
    viewer?.regionStateVersion.value;
    return modelTreeItems.value
        .filter((item) => item.visible)
        .flatMap((item) => viewer?.getRegionRuntime(item.region.id)?.models ?? [])
        .filter((object) => object.visible);
});

function setRegionPending(regionId: string, pending: boolean) {
    const next = new Set(pendingRegionIds.value);
    if (pending) next.add(regionId);
    else next.delete(regionId);
    pendingRegionIds.value = [...next];
}

async function updateVisibleRegions(regionIds: string[], preferredActiveRegionId: string | null) {
    if (!viewer) return;
    await viewer.selectRegions(regionIds, preferredActiveRegionId);
    viewerEpoch.value++;
}

async function unloadModel(regionId: string) {
    if (!viewer) return;
    const region = viewer.availableRegions.find((item) => item.id === regionId);
    if (!region?.enabled) return;
    setRegionPending(regionId, true);
    try {
        await updateVisibleRegions(
            viewer.selectedRegionIds.value.filter((id) => id !== regionId),
            viewer.activeRegionId.value,
        );
    } catch (error) {
        showSnackbar(`Failed to unload model: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
        setRegionPending(regionId, false);
    }
}

async function loadAndActivateModel(regionId: string) {
    if (!viewer) return;
    const region = viewer.availableRegions.find((item) => item.id === regionId);
    if (!region?.enabled) return;

    if (!viewer.selectedRegionIds.value.includes(regionId)) {
        await updateVisibleRegions([...viewer.selectedRegionIds.value, regionId], regionId);
        return;
    }

    await viewer.loadRegion(regionId);
    if (region.canActivate) {
        await viewer.setActiveRegion(regionId);
        viewerEpoch.value++;
    }
}

async function toggleModelLoaded(item: ModelTreeItem, shouldLoad: boolean | null) {
    if (shouldLoad === null) return;
    if (!shouldLoad) {
        await unloadModel(item.region.id);
        return;
    }

    setRegionPending(item.region.id, true);
    try {
        await loadAndActivateModel(item.region.id);
    } catch (error) {
        showSnackbar(`Failed to load model: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
        setRegionPending(item.region.id, false);
    }
}

async function toggleAllModelsVisibility() {
    if (!viewer) return;
    const regionIds = modelsAllVisible.value
        ? []
        : enabledModelTreeItems.value.map((item) => item.region.id);
    pendingRegionIds.value = [...regionIds];
    try {
        await updateVisibleRegions(regionIds, viewer.activeRegionId.value);
    } catch (error) {
        showSnackbar(`Failed to update model visibility: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
        pendingRegionIds.value = [];
    }
}

async function activateModel(regionId: string) {
    if (!viewer) return;
    const region = viewer.availableRegions.find((item) => item.id === regionId);
    if (!region?.enabled) return;
    setRegionPending(regionId, true);
    try {
        await loadAndActivateModel(regionId);
        if (!region.canActivate) {
            const models = viewer.getRegionRuntime(regionId)?.models ?? [];
            if (models.length) viewer.zoomToObjects(models);
        }
    } catch (error) {
        showSnackbar(`Failed to activate model: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
        setRegionPending(regionId, false);
    }
}

async function zoomToModel(regionId: string) {
    if (!viewer) return;
    const region = viewer.availableRegions.find((item) => item.id === regionId);
    if (!region?.enabled) return;
    setRegionPending(regionId, true);
    try {
        await loadAndActivateModel(regionId);
        const models = viewer.getRegionRuntime(regionId)?.models ?? [];
        if (models.length) viewer.zoomToObjects(models);
    } catch (error) {
        showSnackbar(`Failed to zoom to model: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
        setRegionPending(regionId, false);
    }
}

function zoomToModels() {
    if (viewer && visibleModelObjects.value.length) {
        viewer.zoomToObjects(visibleModelObjects.value);
    }
}

const precisionCommandActive = computed(() => {
    viewerEpoch.value;
    const command = viewer?.currentCommand.value;
    return command instanceof ttCmd.CreatePolylineCmd
        || command instanceof ttCmd.CreateMarkerCommand
        || command instanceof ttCmd.GetDistanceCmd;
});

const regionErrorMessage = computed(() => {
    viewerEpoch.value;
    viewer?.regionStateVersion.value;
    for (const region of viewer?.availableRegions ?? []) {
        const runtime = viewer?.getRegionRuntime(region.id);
        if (runtime?.error) return runtime.error;
    }
    return '';
});

const cursorPositionText = computed(() => {
    viewerEpoch.value;
    const position = viewer?.cursorWorldPosition;
    if (!position?.valid) return 'X: —   Y: —   Z: —';
    const source = position.source === 'model' ? 'MODEL' : 'Z=0 PLANE';
    return `X: ${position.x.toFixed(3)}   Y: ${position.y.toFixed(3)}   Z: ${position.z.toFixed(3)}   ${source}`;
});

const lightValue = ref<number>(1.0);
const lightValueMax = 10.0;
function changeLightValue() {
    if (viewer) {
        const ambientLight : THREE.AmbientLight = viewer.scene.getObjectByName("ambient_light");
        if (ambientLight)
            ambientLight.intensity = lightValue.value;
    }
}

const pointSizeValue = ref<number>(0.014);
function changePointSizeValue() {
    if (viewer) {
        if (viewer.pointCloudSize)
            pointCloudSize = pointSizeValue.value;
    }
}

const searchQuery = ref('');

const filteredLines = computed(() => {
    if (!searchQuery.value) {
        return lines.value;
    }
    return filterObjects(lines.value, searchQuery.value);
});

const lineTreeItems = computed(() => {
    viewerEpoch.value;
    return filteredLines.value.filter((line) => !line.isMarker());
});
const markerTreeItems = computed(() => {
    viewerEpoch.value;
    return filteredLines.value.filter((line) => line.isMarker());
});

watch(searchQuery, (query) => {
    if (query?.trim()) openTreeNodes.value = ['models', 'trajectories', 'lines', 'markers', 'pointClouds'];
});

const linesAllVisible = computed(() => {
    viewerEpoch.value;
    return lineTreeItems.value.length > 0 && lineTreeItems.value.every((line) => line.obj.visible);
});
const linesAnyVisible = computed(() => {
    viewerEpoch.value;
    return lineTreeItems.value.some((line) => line.obj.visible);
});
const markersAllVisible = computed(() => {
    viewerEpoch.value;
    return markerTreeItems.value.length > 0 && markerTreeItems.value.every((marker) => marker.obj.visible);
});
const markersAnyVisible = computed(() => {
    viewerEpoch.value;
    return markerTreeItems.value.some((marker) => marker.obj.visible);
});

function visibilityIcon(allVisible: boolean, anyVisible: boolean) {
    if (allVisible) return 'mdi-eye-outline';
    return anyVisible ? 'mdi-eye-settings-outline' : 'mdi-eye-off-outline';
}

function lineIndex(line: Polyline) {
    return viewer?.lines.indexOf(line) ?? -1;
}

function setLinesVisibility(items: Polyline[], visible: boolean) {
    if (!viewer) return;
    for (const line of items) {
        line.obj.visible = visible;
        if (line.svg && !visible) line.svg.style.display = 'none';
    }
    viewer.refreshProjectedLines();
    viewer.updateOnControlChange();
    viewerEpoch.value++;
}

function toggleLineVisibility(line: Polyline) {
    setLinesVisibility([line], !line.obj.visible);
}

function toggleAllLineVisibility() {
    setLinesVisibility(lineTreeItems.value, !linesAllVisible.value);
}

function toggleAllMarkerVisibility() {
    setLinesVisibility(markerTreeItems.value, !markersAllVisible.value);
}

function zoomToLines() {
    if (viewer && lineTreeItems.value.length) {
        viewer.zoomToObjects(lineTreeItems.value.map((line) => line.obj));
    }
}

function zoomToMarkers() {
    if (viewer && markerTreeItems.value.length) {
        viewer.zoomToObjects(markerTreeItems.value.map((marker) => marker.obj));
    }
}

const pointCloudTreeItems = computed(() => {
    if (!searchQuery.value) {
        return pointClouds.value;
    }
    return filterObjects(pointClouds.value, searchQuery.value);
});

const pointCloudsAllVisible = computed(() => {
    viewerEpoch.value;
    return pointCloudTreeItems.value.length > 0 && pointCloudTreeItems.value.every((points) => points.visible);
});
const pointCloudsAnyVisible = computed(() => {
    viewerEpoch.value;
    return pointCloudTreeItems.value.some((points) => points.visible);
});

function toggleAllPointCloudVisibility() {
    setPointCloudsVisibility(pointCloudTreeItems.value, !pointCloudsAllVisible.value);
}

function zoomToPointClouds() {
    if (viewer && pointCloudTreeItems.value.length) {
        viewer.zoomToObjects(pointCloudTreeItems.value.map((points) => viewer.getProjectPointCloudRuntime(points.id).pointCloud));
    }
}

function setPointCloudsVisibility(items: ProjectPointCloud[], visible: boolean) {
    if (!viewer) return;
    for (const pointCloud of items) {
        pointCloud.visible = visible;
        const model = viewer.getProjectPointCloudRuntime(pointCloud.id).pointCloud;
        if(model)
            model.visible = visible;
    }
    viewerEpoch.value++;
}

function togglePointCloudVisibility(point: ProjectPointCloud) {
    setPointCloudsVisibility([point], !point.visible);
}

async function zoomToPointCloud(pointCloud: PointCloud) {
    if (viewer && pointCloud) {
        // if(!pointCloud.loaded){
        //     activatePointCloud(pointCloud.id)
        // }
        // if(!pointCloud.visible){
        //     togglePointCloudVisibility(pointCloud);
        // }
        viewer.zoomToObjects(pointCloudTreeItems.value.filter((p) => p === pointCloud).map(
            (p) => viewer.getProjectPointCloudRuntime(p.id).pointCloud)
        );
    }
}

async function activatePointCloud(pointCloudId: string) {
    if (!viewer) return;
    const pointCloud = viewer.projectPointClouds.find((item) => item.id === pointCloudId);
    if (!pointCloud?.enabled) return;
    setPointCloudPending(pointCloudId, true);
    try {
        await viewer.togglePointCloud(pointCloudId);
        pointClouds.value = viewer.projectPointClouds;
        zoomToPointCloud(viewer.getProjectPointCloudRuntime(pointCloudId).pointCloud);
    } catch (error) {
        showSnackbar(`Failed to activate model: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
        setPointCloudPending(pointCloudId, false);
    }
}

function setPointCloudPending(pointCloudId: string, pending: boolean) {
    const next = new Set(pendingPointCloudsIds.value);
    if (pending) next.add(pointCloudId);
    else next.delete(pointCloudId);
    pendingPointCloudsIds.value = [...next];
}

async function unloadPointCloud(pointCloud: ProjectPointCloud) {
    if (!viewer || !pointCloud) return;
    if (!pointCloud.loaded || !pointCloud.enabled) return;
    setPointCloudPending(pointCloud.id, true);
    try {
        await viewer.togglePointCloud(pointCloud.id);
        pointClouds.value = viewer.projectPointClouds;
    } catch (error) {
        showSnackbar(`Failed to unload point cloud: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
        setPointCloudPending(pointCloudId, false);
    }
}

async function loadPointCloud(pointCloud: ProjectPointCloud) {
    if (!viewer || !pointCloud) return;
    if (!pointCloud?.enabled) return;

    pointCloud.loading = true;


    await viewer.togglePointCloud(pointCloud.id);
    pointCloud.loading = false;
    pointClouds.value = viewer.projectPointClouds;
}

async function togglePointCloudLoaded(pointCloud: ProjectPointCloud, shouldLoad: boolean | null) {
    if (shouldLoad === null || !pointCloud) return;
    if (!shouldLoad) {
        await unloadPointCloud(pointCloud);
        return;
    }

    setPointCloudPending(pointCloud.id, true);
    try {
        await loadPointCloud(pointCloud);
    } catch (error) {
        showSnackbar(`Failed to load point cloud: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
        setPointCloudPending(pointCloud.id, false);
    }
}

function filterObjects(items, query: string) {
    if (!query)
        return items;
    query = query.trim().toLocaleUpperCase();
    if (query.length === 0)
        return items;
    const eqindex = query.indexOf("=");
    if (eqindex === -1) {
        return items.filter(item => {
            if (item.name.toLocaleUpperCase().includes(query))
                return true;
            return Object.entries(item.custom).some(([prop, value]) => {
                if (prop.toLocaleUpperCase().includes(query))
                    return true;
                if (String(value).toLocaleUpperCase().includes(query))
                    return true;
                return false;
            });
        });
    }
    else {
        const propq = query.substring(0, eqindex).trim().toLocaleUpperCase();
        const valueq = query.substring(eqindex + 1, query.length).trim().toLocaleUpperCase();
        if (propq === "SEVERITY") {
        }
        return items.filter(item => {
            if (propq === "NAME") {
                if (item.name.toLocaleUpperCase().includes(valueq))
                    return true;
                return false;
            }
            else if (propq === "SEVERITY" && item.severity !== undefined) {
                if (valueq === "LOW")
                    return item.severity === 1;
                else if (valueq === "NORMAL" || valueq === "MEDIUM")
                    return item.severity === 2;
                else if (valueq === "HIGH")
                    return item.severity === 3;
                return false;
            }
            return Object.entries(item.custom).some(([prop, value]) => {
                if (prop.toLocaleUpperCase() === propq) {
                    return String(value).toLocaleUpperCase().includes(valueq);
                }
                return false;
            });
        });
    }
}

function openUrl() {
    if (selectedLines.value[0].url.includes("://")) {
        window.open(selectedLines.value[0].url);
    }
    else {
        window.open("http://" + selectedLines.value[0].url);
    }
}

const snackbar = ref(false);
const snackbarText = ref("");
let snackbarTimeout = 3000;
function showSnackbar(message, timeout = 3000) {
    snackbarText.value = message;
    snackbar.value = true;
    snackbarTimeout = timeout;
}
const snackbarTextComputed = () => {
    if (typeof snackbarText.value === "string") {
        return snackbarText.value;
    }
    else {
        return snackbarText.value();
    }
}

function validateUrl(url: string): boolean {
    // TODO does the url lead to a valid resource? For now, just check if it is a non-empty string.
    return url.trim().length > 0;
}

function importModelFromFile() {
    //select point cloud file and append the loaded point cloud to the current region. The point cloud file should be in .ply format.
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.ply,.glb,.glb,.obj,.pcd,.las,.laz';
    input.onchange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (!target.files || target.files.length === 0) {
            return;
        }
        const file = target.files[0];
        if (viewer) {
            try {
                if (!file.name.toLowerCase().endsWith('.ply') 
                    && !file.name.toLowerCase().endsWith('.glb') 
                    && !file.name.toLowerCase().endsWith('.obj') 
                    //&& !file.name.toLowerCase().endsWith('.pcd') 
                    //&& !file.name.toLowerCase().endsWith('.las') 
                    //&& !file.name.toLowerCase().endsWith('.laz')
                ) {
                    showSnackbar(`Unsupported file format: ${file.name}`);
                    return;
                }
                if(file.name.toLowerCase().endsWith('.ply')){
                    await viewer.importPointCloudFromFile(file);
                    showSnackbar(`Successfully imported point cloud from ${file.name}`);
                    pointClouds.value = viewer.projectPointClouds;
                }else if(file.name.toLowerCase().endsWith('.glb') || file.name.toLowerCase().endsWith('.obj')){
                    await viewer.importGlbModelFromFile(file);
                    showSnackbar(`Successfully imported model from ${file.name}`);
                }
            }
            catch (error) {
                showSnackbar(`Error importing model from ${file.name}: ${error}`);
            }
        }
    };
    input.click();
}

</script>

<style scoped>
.viewer-main {
    position: relative;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
}

.viewer-content {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.viewport {
    padding: 0;
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    width: 100%;
}

#mainCanvasDivId {
    position: relative;
}

canvas {
    width: 100%;
    height: 100%;
}

.divider {
    width: 40px;
    background-color: lightgray;
    transition: background-color 0.3s ease;
}

.divider:hover {
    background-color: black;
}

.floating-window {
    position: absolute;
    display: flex;
    flex-direction: column;
    border: 1px solid #ccc;
    background-color: #fff;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.floating-window-header {
    flex: 0 0 auto;
    margin: 5px;
    cursor: move;
    display: flex;
    justify-content: space-between;
    align-items: center;
    user-select: none;
}

.floating-window > .viewport {
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
}

.floating-window canvas {
    display: block;
}

.resize-handle {
    position: absolute;
    width: 15px;
    height: 15px;
    background-color: transparent;
}

.resize-handle.top-left {
    top: -7px;
    left: -7px;
    cursor: nwse-resize;
}

.resize-handle.top-right {
    top: -7px;
    right: -7px;
    cursor: nesw-resize;
}

.resize-handle.bottom-left {
    bottom: -7px;
    left: -7px;
    cursor: nesw-resize;
}

.resize-handle.bottom-right {
    bottom: -7px;
    right: -7px;
    cursor: nwse-resize;
}

.object-search {
    margin: 12px 12px 4px;
}

.object-tree {
    overflow-x: hidden;
}

.tree-item {
    padding-inline-start: 28px !important;
}

.tree-item :deep(.v-list-item-subtitle) {
    font-size: 0.7rem;
    line-height: 1rem;
}

.object-tree :deep(.v-list-item__append) {
    gap: 8px;
}

.command-crosshair,
.command-crosshair canvas {
    cursor: crosshair !important;
}

.region-error {
    position: absolute;
    z-index: 20;
    bottom: 16px;
    left: 50%;
    width: min(760px, calc(100% - 32px));
    transform: translateX(-50%);
}

.cursor-status-bar {
    position: relative;
    z-index: 15;
    flex: 0 0 24px;
    width: 100%;
    height: 24px;
    min-height: 24px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 12px;
    color: rgba(255, 255, 255, 0.92);
    background: rgba(25, 25, 25, 0.88);
    border-top: 1px solid rgba(255, 255, 255, 0.16);
    font-family: Consolas, 'Courier New', monospace;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    pointer-events: none;
}

.cursor-coordinate {
    white-space: pre;
}

</style>
